import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { Lock, Sparkles, ArrowRight, RotateCcw } from "lucide-react";
import { EASE, stagger, fadeUp, viewportOnce } from "@/lib/motion";
import { lifePathFromDate, expressionFromName } from "@/lib/numerology";
import { LIFE_PATHS, PREMIUM_INSIGHTS } from "@/data/numerology";
import { useSound } from "@/context/SoundContext";

const Numerology = () => {
  const { playChime } = useSound();
  const [firstName, setFirstName] = useState("");
  const [dob, setDob] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef(null);
  const today = new Date().toISOString().split("T")[0];

  const handleReveal = (e) => {
    e.preventDefault();
    if (!dob) {
      setError("Please enter your date of birth");
      return;
    }
    const lifePath = lifePathFromDate(dob);
    if (!lifePath || !LIFE_PATHS[lifePath]) {
      setError("Please enter a valid date of birth");
      return;
    }
    setError("");
    setLoading(true);

    const expression = expressionFromName(firstName);
    const expressionArchetype =
      expression && LIFE_PATHS[expression] ? LIFE_PATHS[expression].archetype : null;

    // Short, elegant "reading" moment before revealing (client-side only).
    timer.current = setTimeout(() => {
      setResult({
        ...LIFE_PATHS[lifePath],
        lifePath,
        expression,
        expressionArchetype,
        dob,
        name: firstName.trim(),
      });
      setLoading(false);
      // A single soft shimmering bell only when the result appears (if sound on).
      playChime();
    }, 1900);
  };

  const reset = () => {
    if (timer.current) clearTimeout(timer.current);
    setResult(null);
    setLoading(false);
    setDob("");
    setFirstName("");
  };

  return (
    <section
      id="numerology"
      data-testid="numerology-section"
      className="relative overflow-hidden bg-forest text-ivory grain scroll-mt-24"
    >
      {/* soft radial glow */}
      <div
        className="pointer-events-none absolute -top-1/3 left-1/2 -translate-x-1/2 h-[80vw] w-[80vw] max-h-[900px] max-w-[900px] rounded-full opacity-40"
        style={{ background: "radial-gradient(circle, rgba(201,169,110,0.18) 0%, rgba(47,79,70,0) 60%)" }}
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 py-28 md:py-44">
        <AnimatePresence mode="wait">
          {/* ── Loading ─────────────────────────────── */}
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              data-testid="numerology-loading"
              className="min-h-[340px] flex flex-col items-center justify-center text-center"
            >
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 5, ease: "linear", repeat: Infinity }}
                className="font-serif text-gold text-6xl md:text-7xl leading-none"
              >
                ✦
              </motion.span>
              <motion.p
                initial={{ opacity: 0.4 }}
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 2, ease: "easeInOut", repeat: Infinity }}
                className="mt-10 font-serif font-light italic text-2xl md:text-3xl text-ivory/80"
              >
                Reading your personal blueprint…
              </motion.p>
              <p className="mt-4 kicker text-ivory/40 text-[0.6rem]">Attuning to your numbers</p>
            </motion.div>
          ) : !result ? (
            /* ── Intake ─────────────────────────────── */
            <motion.div
              key="intake"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-center"
            >
              <div className="lg:col-span-6">
                <Reveal>
                  <p className="kicker text-gold mb-6 flex items-center gap-3">
                    <Sparkles size={14} strokeWidth={1.5} /> A gift from Svara Chakra
                  </p>
                </Reveal>
                <Reveal delay={0.06}>
                  <h2 className="font-serif font-light text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
                    Discover your <span className="italic text-gold">numerology</span> blueprint.
                  </h2>
                </Reveal>
                <Reveal delay={0.12}>
                  <p className="mt-8 text-ivory/70 leading-relaxed max-w-lg md:text-lg">
                    Hidden within your date of birth is your Life Path — a quiet map
                    of who you are and who you are becoming. Add your first name and
                    we will also reveal your Expression, the vibration of your name.
                  </p>
                </Reveal>

                <Reveal delay={0.18}>
                  <form onSubmit={handleReveal} className="mt-12 max-w-md space-y-8" data-testid="numerology-form">
                    <div>
                      <label className="kicker text-ivory/50 text-[0.62rem] block mb-3">
                        First name <span className="text-ivory/30 normal-case tracking-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="e.g. Anaïs"
                        data-testid="numerology-name"
                        className="w-full bg-transparent border-b border-ivory/30 py-4 text-ivory text-lg placeholder:text-ivory/30 focus:outline-none focus:border-gold transition-colors duration-300 rounded-none"
                      />
                    </div>
                    <div>
                      <label className="kicker text-ivory/50 text-[0.62rem] block mb-3">
                        Your date of birth
                      </label>
                      <input
                        type="date"
                        value={dob}
                        max={today}
                        onChange={(e) => {
                          setDob(e.target.value);
                          setError("");
                        }}
                        data-testid="numerology-dob"
                        className="w-full bg-transparent border-b border-ivory/30 py-4 text-ivory text-lg focus:outline-none focus:border-gold transition-colors duration-300 rounded-none [color-scheme:dark]"
                      />
                    </div>
                    {error && (
                      <p className="text-sm text-gold/90" data-testid="numerology-error">
                        {error}
                      </p>
                    )}
                    <button
                      type="submit"
                      data-testid="numerology-reveal"
                      className="group inline-flex items-center gap-3 bg-ivory text-forest px-9 py-4 uppercase tracking-[0.16em] text-xs transition-colors duration-500 hover:bg-gold"
                    >
                      Reveal My Blueprint
                      <ArrowRight size={16} strokeWidth={1.6} className="transition-transform duration-500 group-hover:translate-x-1" />
                    </button>
                  </form>
                </Reveal>
              </div>

              {/* decorative numeral cluster */}
              <div className="lg:col-span-5 lg:col-start-8 hidden lg:block">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={viewportOnce}
                  transition={{ duration: 1.2, ease: EASE }}
                  className="relative aspect-square border border-ivory/15 rounded-full flex items-center justify-center"
                >
                  <div className="absolute inset-8 border border-ivory/10 rounded-full" />
                  <div className="absolute inset-16 border border-gold/20 rounded-full" />
                  <span className="font-serif font-light text-gold/90 text-[12rem] leading-none animate-float-slow">
                    ✦
                  </span>
                  {["Sa", "Re", "Ga", "Ma", "Pa", "Dha", "Ni"].map((s, i, arr) => {
                    const angle = (360 / arr.length) * i - 90;
                    const r = 44;
                    const x = 50 + r * Math.cos((angle * Math.PI) / 180);
                    const y = 50 + r * Math.sin((angle * Math.PI) / 180);
                    return (
                      <span
                        key={s}
                        className="absolute font-serif text-ivory/50 text-lg"
                        style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%,-50%)" }}
                      >
                        {s}
                      </span>
                    );
                  })}
                </motion.div>
              </div>
            </motion.div>
          ) : (
            /* ── Result ─────────────────────────────── */
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
              data-testid="numerology-result"
            >
              {result.name && (
                <Reveal className="mb-8">
                  <p className="kicker text-ivory/50">
                    A first reading for <span className="text-gold">{result.name}</span>
                  </p>
                </Reveal>
              )}

              {/* Editorial card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: EASE, delay: 0.1 }}
                className="border border-ivory/15 bg-ivory/[0.03] backdrop-blur-sm"
              >
                {/* Numbers header */}
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-ivory/12 border-b border-ivory/12">
                  <div className="flex items-center gap-8 p-10 md:p-14">
                    <span
                      data-testid="numerology-number"
                      className="font-serif font-light text-gold text-[7rem] md:text-[9rem] leading-none"
                    >
                      {result.lifePath}
                    </span>
                    <div>
                      <p className="kicker text-ivory/50 mb-2">Life Path Number</p>
                      <h3 className="font-serif font-light text-3xl md:text-4xl leading-tight">
                        {result.archetype}
                      </h3>
                    </div>
                  </div>

                  {result.expression ? (
                    <div className="flex items-center gap-8 p-10 md:p-14" data-testid="numerology-expression-block">
                      <span
                        data-testid="numerology-expression"
                        className="font-serif font-light text-sage text-[7rem] md:text-[9rem] leading-none"
                      >
                        {result.expression}
                      </span>
                      <div>
                        <p className="kicker text-ivory/50 mb-2">Expression · Name Vibration</p>
                        <h3 className="font-serif font-light text-3xl md:text-4xl leading-tight">
                          {result.expressionArchetype}
                        </h3>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center p-10 md:p-14">
                      <p className="text-ivory/45 font-light leading-relaxed max-w-xs">
                        Add your first name to also reveal your Expression Number —
                        the vibration held within your name.
                      </p>
                    </div>
                  )}
                </div>

                {/* Overview + strengths + growth */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 p-10 md:p-14">
                  <div className="lg:col-span-6">
                    <p className="kicker text-gold mb-5">Personality Overview</p>
                    <p className="font-serif font-light text-2xl md:text-3xl leading-[1.4] text-ivory/90 text-balance">
                      {result.overview}
                    </p>
                  </div>
                  <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <p className="kicker text-gold mb-5">Natural Strengths</p>
                      <ul className="space-y-3">
                        {result.strengths.map((s) => (
                          <li key={s} className="flex items-start gap-3 text-ivory/80">
                            <span className="mt-2 h-1 w-1 rounded-full bg-gold shrink-0" />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="kicker text-gold mb-5">Growth Areas</p>
                      <ul className="space-y-3">
                        {result.growth.map((g) => (
                          <li key={g} className="flex items-start gap-3 text-ivory/80">
                            <span className="mt-2 h-1 w-1 rounded-full bg-sage shrink-0" />
                            {g}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Preview note */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.9 }}
                className="text-center max-w-2xl mx-auto py-16 md:py-20"
              >
                <p className="font-serif font-light italic text-2xl md:text-3xl text-ivory/80 leading-[1.5]">
                  Every number tells part of your story. Together, they reveal
                  something deeper.
                </p>
                <p className="mt-8 text-ivory/55 leading-relaxed md:text-lg">
                  The insights you've discovered today are only the beginning. A
                  complete personal reading brings together your Life Path,
                  Expression, hidden patterns, personal cycles and deeper themes to
                  create a more meaningful understanding of your journey.
                </p>
                <p className="mt-8 font-serif font-light italic text-xl md:text-2xl text-gold">
                  No two blueprints are ever the same.
                </p>
              </motion.div>

              {/* Locked premium insights */}
              <motion.div
                variants={stagger(0.05, 0.1)}
                initial="hidden"
                whileInView="visible"
                viewport={viewportOnce}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {PREMIUM_INSIGHTS.map((insight, i) => (
                  <motion.div
                    key={insight.title}
                    variants={fadeUp}
                    data-testid={`premium-insight-${i}`}
                    className="group relative border border-ivory/15 bg-ivory/[0.03] p-8 overflow-hidden transition-colors duration-500 hover:border-gold/40"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-serif font-light text-2xl text-ivory">{insight.title}</h4>
                      <span className="flex h-8 w-8 items-center justify-center rounded-full border border-gold/40 text-gold shrink-0 transition-colors duration-500 group-hover:bg-gold/10">
                        <Lock size={13} strokeWidth={1.6} />
                      </span>
                    </div>
                    <p className="text-ivory/60 leading-relaxed text-sm blur-[5px] select-none" aria-hidden="true">
                      {insight.teaser}
                    </p>
                    <p className="mt-4 kicker text-ivory/40 text-[0.58rem]">Locked · Premium insight</p>
                  </motion.div>
                ))}

                {/* CTA card */}
                <motion.div
                  variants={fadeUp}
                  className="relative border border-gold/40 bg-gold/10 p-8 flex flex-col justify-between"
                >
                  <p className="font-serif font-light text-2xl text-ivory leading-snug">
                    Your complete reading awaits.
                  </p>
                  <Link
                    to="/contact"
                    state={{
                      blueprint: {
                        firstName: result.name,
                        dob: result.dob,
                        lifePath: result.lifePath,
                        expression: result.expression,
                      },
                    }}
                    data-testid="numerology-cta"
                    className="group mt-6 inline-flex items-center gap-3 text-gold uppercase tracking-[0.14em] text-xs"
                  >
                    Unlock Your Complete Personal Reading
                    <ArrowRight size={15} strokeWidth={1.6} className="transition-transform duration-500 group-hover:translate-x-1" />
                  </Link>
                </motion.div>
              </motion.div>

              {/* Primary CTA + reset */}
              <div className="mt-16">
                <p className="text-center font-serif font-light italic text-2xl md:text-3xl text-ivory/85 mb-10">
                  Your story deserves more than a single number.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <Link
                  to="/contact"
                  state={{
                    blueprint: {
                      firstName: result.name,
                      dob: result.dob,
                      lifePath: result.lifePath,
                      expression: result.expression,
                    },
                  }}
                  data-testid="numerology-cta-primary"
                  className="group inline-flex items-center gap-3 bg-ivory text-forest px-10 py-5 uppercase tracking-[0.16em] text-xs transition-colors duration-500 hover:bg-gold"
                >
                  Unlock Your Complete Personal Reading
                  <ArrowRight size={16} strokeWidth={1.6} className="transition-transform duration-500 group-hover:translate-x-1" />
                </Link>
                <button
                  onClick={reset}
                  data-testid="numerology-reset"
                  className="inline-flex items-center gap-2 text-ivory/60 hover:text-ivory transition-colors duration-300 text-xs uppercase tracking-[0.14em]"
                >
                  <RotateCcw size={14} strokeWidth={1.5} /> Try another reading
                </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Numerology;
